// Document Version Management System
// Tracks changes and versions for DSGVO compliance documents

export interface DocumentChange {
  id: string;
  date: string;
  author: string;
  description: string;
  version: string;
}

export interface DocumentVersion {
  documentId: string;
  currentVersion: string;
  lastModified: string;
  changes: DocumentChange[];
}

const STORAGE_KEY = 'dsgvo-document-versions';

export const getDocumentVersion = (documentId: string): DocumentVersion | null => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  
  const versions: Record<string, DocumentVersion> = JSON.parse(stored);
  return versions[documentId] || null;
};

export const updateDocumentVersion = (
  documentId: string,
  change: Omit<DocumentChange, 'id' | 'date' | 'version'>
): DocumentVersion => {
  const stored = localStorage.getItem(STORAGE_KEY);
  const versions: Record<string, DocumentVersion> = stored ? JSON.parse(stored) : {};
  
  const currentDoc = versions[documentId] || {
    documentId,
    currentVersion: '1.0',
    lastModified: new Date().toISOString(),
    changes: []
  };

  // Calculate next version
  const versionParts = currentDoc.currentVersion.split('.');
  const minorVersion = parseInt(versionParts[1] || '0');
  const newVersion = `${versionParts[0]}.${minorVersion + 1}`;

  const newChange: DocumentChange = {
    id: `change-${Date.now()}`,
    date: new Date().toISOString(),
    author: change.author,
    description: change.description,
    version: newVersion
  };

  const updatedDoc: DocumentVersion = {
    ...currentDoc,
    currentVersion: newVersion,
    lastModified: new Date().toISOString(),
    changes: [newChange, ...currentDoc.changes]
  };

  versions[documentId] = updatedDoc;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(versions));

  return updatedDoc;
};

export const getAllDocumentVersions = (): Record<string, DocumentVersion> => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? JSON.parse(stored) : {};
};

export const initializeDocument = (
  documentId: string,
  initialVersion: string = '1.0'
): DocumentVersion => {
  const existing = getDocumentVersion(documentId);
  if (existing) return existing;

  const initialDoc: DocumentVersion = {
    documentId,
    currentVersion: initialVersion,
    lastModified: new Date().toISOString(),
    changes: [{
      id: 'initial',
      date: new Date().toISOString(),
      author: 'System',
      description: 'Initiale Dokumenterstellung',
      version: initialVersion
    }]
  };

  const stored = localStorage.getItem(STORAGE_KEY);
  const versions: Record<string, DocumentVersion> = stored ? JSON.parse(stored) : {};
  versions[documentId] = initialDoc;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(versions));

  return initialDoc;
};
